import {useMemo, useContext} from 'react'
import {SegmentContext} from '../contexts'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {productUrlBuilder} from '../../../../../app/utils/url'
import {
    commonParams,
    productParams,
    utmParams,
    pannelparams,
    getPanelDomData,
    getProductListViewedDomData,
    productViewedParams,
    getDeviceTypeByScreenSize,
    feedContextprams,
    getCartViewedDomData,
    getFilterAppliedDomData
} from '../utils/segment-utils'

export class SegmentAPI {
    constructor(segmentContext) {
        this.segmentContext = segmentContext
    }

    /**
     * Constructs an object containing the product Id.
     */
    _constructSegmentProduct(item) {
        return {
            id: item.productId,
            price: item.price,
            quantity: item.quantity
        }
    }
    _removeEmptyKeysFromObject(obj) {
        if (Array.isArray(obj)) {
            return obj
                .map((item) => this._removeEmptyKeysFromObject(item))
                .filter((item) => this._isValid(item))
        }

        if (obj && typeof obj === 'object') {
            const cleanedObj = Object.entries(obj).reduce((acc, [key, value]) => {
                const cleanedValue = this._removeEmptyKeysFromObject(value)
                if (this._isValid(cleanedValue)) {
                    acc[key] = cleanedValue
                }
                return acc
            }, {})
            return cleanedObj
        }

        return obj
    }

    _isValid(value) {
        if (value === null || value === undefined || value === '') return false
        if (typeof value === 'object') return Object.keys(value).length > 0
        return true
    }

    /**
     * returns inventory status for product
     */
    _inventoryStatus(product) {
        if (product?.inventory?.preorderable) {
            return 'Pre-Order'
        } else if (product?.inventory?.backorderable && product?.inventory?.stockLevel <= 0) {
            return 'Backorder'
        } else if (product?.inventory?.ats === 0) {
            return 'Out of Stock'
        } else {
            return 'In Stock'
        }
    }

    /**
     * Build product URL for Segment tracking
     */
    _buildProductUrl(product, appOrigin) {
        const prodObj = {
            productName: product?.productName || product?.name,
            productId: product?.productId || product?.id
        }
        return `${appOrigin}${productUrlBuilder(prodObj)}`
    }

    /**
     * return coupon items from basket
     */
    _getCoupons(basket) {
        if (basket?.couponItems?.length > 0) {
            return basket?.couponItems[0]
        }
    }

    /**
     * return empty product details object for Panel Selected
     */
    _emptyProductDetails() {
        return {
            brand: 'null',
            category: 'null',
            productName: 'null',
            price: null,
            sku: 'null'
        }
    }

    /**
     * Tells the Segemnet engine when a user adds an item to their cart.
     */
    async sendAddToCart(product, basket, event) {
        try {
            const [commonParam, productData, utmParameters] = await Promise.all([
                commonParams(this.segmentContext),
                productParams(this.segmentContext, product, basket),
                utmParams()
            ])

            const feedParams = feedContextprams(event?.target) // call sync
            const params = {
                ...commonParam,
                ...productData,
                ...feedParams,
                ...utmParameters,
                inventoryStatus: this._inventoryStatus(product),
                cartid: basket?.basketId,
                coupon: this._getCoupons(basket)
            }
            const segmentParams = this._removeEmptyKeysFromObject(params)

            window?.analytics?.track('Product Added', segmentParams, this.segmentContext?.context)
        } catch (error) {
            logger.error('segment- sendAddToCart failed', error)
        }
    }

    /**
     * Tells the Segemnet engine when a user adds an item to their cart.
     */
    async sendProductClick(product, productPosition, event) {
        try {
            const currentElement = event?.target
            const [commonParam, productData, utmParameters] = await Promise.all([
                commonParams(this.segmentContext),
                productParams(this.segmentContext, product),
                utmParams()
            ])
            const params = {
                ...commonParam,
                ...productData,
                ...utmParameters,
                inventoryStatus: this._inventoryStatus(product),
                position: productPosition,
                url: product?.slugUrl
            }
            let segmentParams = this._removeEmptyKeysFromObject(params)
            // handling only for carousels
            if (currentElement?.closest('.slick-slide')) {
                const feedParams = feedContextprams(currentElement)
                segmentParams.feedTitle = feedParams.feedTitle
                segmentParams.feedType = feedParams.feedType
            }

            window?.analytics?.track('Product Clicked', segmentParams, this.segmentContext?.context)
        } catch (error) {
            logger.error('segment- sendProductClick failed', error)
        }
    }

    /**
     * Tells the Segemnet engine when a user adds an item to their wishlist.
     */
    async sendProductToWishList(product, wishlist, productPosition, productDetail, event) {
        try {
            const [commonParam, utmParam, productData] = await Promise.all([
                commonParams(this.segmentContext),
                utmParams(),
                productParams(this.segmentContext, product, null, {}, productDetail)
            ])
            const feedParams = feedContextprams(event?.target)
            const params = {
                ...feedParams,
                ...commonParam,
                ...productData,
                ...utmParam,
                inventoryStatus: this._inventoryStatus(product),
                position: productPosition,
                url: this._buildProductUrl(product, this.segmentContext?.appOrigin),
                wishlistid: wishlist?.id
            }
            const segmentParams = this._removeEmptyKeysFromObject(params)

            window?.analytics?.track(
                'Product Added to Wishlist',
                segmentParams,
                this.segmentContext?.context
            )
        } catch (error) {
            logger.error('segment- sendProductToWishList failed', error)
        }
    }

    /**
     * Tells the Segemnet engine when a user adds an item to their wishlist.
     */
    async sendRemoveProductFromWishlist(product, wishlist, productPosition, productDetail) {
        try {
            const [commonParam, utmParam, productData] = await Promise.all([
                commonParams(this.segmentContext),
                utmParams(),
                productParams(this.segmentContext, product, null, {}, productDetail)
            ])

            const params = {
                ...commonParam,
                ...productData,
                ...utmParam,
                inventoryStatus: this._inventoryStatus(product),
                position: productPosition,
                url: this._buildProductUrl(product, this.segmentContext?.appOrigin),
                wishlistid: wishlist?.id
            }

            const segmentParams = this._removeEmptyKeysFromObject(params)

            window?.analytics?.track(
                'Product Removed from Wishlist',
                segmentParams,
                this.segmentContext?.context
            )
        } catch (error) {
            logger.error('segment- sendRemoveProductFromWishlist failed', error)
        }
    }

    /**
     * Tracks when a user clicks on a promotion banner.
     *
     * @param {Object} promoDetails - The promotion object containing details like calloutMsg and promotionId.
     */
    async sendPromoClicked(promoDetails) {
        try {
            let commonParam = await commonParams(this.segmentContext)

            const params = {
                ...commonParam,
                name: promoDetails?.calloutMsg,
                position: 'plp',
                promotionid: promoDetails?.promotionId
            }
            const segmentParams = this._removeEmptyKeysFromObject(params)

            window?.analytics?.track(
                'Promotion Clicked',
                segmentParams,
                this.segmentContext?.context
            )
        } catch (error) {
            logger.error('segment- sendPromoClicked failed', error)
        }
    }

    /**
     * Tracks when a user views a product detail page.
     * @param {Object} product - The product being viewed.
     * @param {Object} event - to track the current element.
     */
    async sendProductViewed(event, product) {
        try {
            const [commonParam, productData, productViewparams, utmParameters] = await Promise.all([
                commonParams(this.segmentContext),
                productParams(this.segmentContext, product, {}),
                productViewedParams(product),
                utmParams()
            ])
            const feedParams = feedContextprams(event?.target)
            const params = {
                ...feedParams,
                ...commonParam,
                ...productData,
                ...productViewparams,
                ...utmParameters
            }
            const segmentParams = this._removeEmptyKeysFromObject(params)

            window?.analytics?.track('Product Viewed', segmentParams, this.segmentContext?.context)
        } catch (error) {
            logger.error('segment- sendProductViewed failed', error)
        }
    }

    /**
     * Tracks when a user subscribes to be notified when a product is back in stock.
     *
     * @param {string} eventName - The name of the event (e.g., 'Back In Stock Signup').
     * @param {string} email - The user's email address.
     * @param {Object} product - The product object containing back-in-stock details.
     */
    async sendBackInStock(eventName, email, product) {
        try {
            let commonParam = await commonParams(this.segmentContext)

            const productData = {
                cid: commonParam?.cid,
                eventSource: commonParam?.eventSource,
                globalName: product?.c_globalName ?? product.name,
                productName: product?.name ?? product?.productName,
                productSku: product?.id ?? product?.productId,
                shopCountry: commonParam?.shopCountry,
                shopLanguage: commonParam?.shopLanguage,
                shopRegion: commonParam?.shopRegion,
                userEmail: email,
                sessionId: commonParam?.sessionId
            }
            const segmentParams = this._removeEmptyKeysFromObject(productData)

            window?.analytics?.track(eventName, segmentParams, this.segmentContext?.context)
        } catch (error) {
            logger.error('segment- sendBackInStock failed', error)
        }
    }

    /**
     * Tracks when a user subscribes to marketing emails from the footer.
     *
     * @param {string} email - The user's email address.
     */
    async sendFooterEmailSubscribe(email) {
        try {
            const [commonParam, utmParameters] = await Promise.all([
                commonParams(this.segmentContext, email),
                utmParams()
            ])
            const params = {
                ...commonParam,
                ...utmParameters,
                deviceDetails: getDeviceTypeByScreenSize(),
                email: email,
                phoneNumber: '',
                sourceLocation: 'Shop',
                storeDeals: '1',
                time: new Date().toISOString()
            }
            const segmentParams = this._removeEmptyKeysFromObject(params)

            window?.analytics?.track(
                'Web Marketing Opt in',
                segmentParams,
                this.segmentContext?.context
            )
        } catch (error) {
            logger.error('segment- sendFooterEmailSubscribe failed', error)
        }
    }

    /**
     * Tracks when a user interacts with a product panel or carousel item.
     *
     * @param {Event} event - The DOM event triggered by the panel click.
     * @param {Object} product - The associated product object (default is an empty object).
     * @param {Object} shopbyseries - Optional metadata about the carousel or series component.
     */
    async sendPanelSelectedEvent(event, product = {}, shopbyseries) {
        try {
            const pannelDomData = getPanelDomData(event, shopbyseries)
            const pannelParams = await pannelparams(
                pannelDomData,
                this.segmentContext,
                product,
                event
            )
            let segmentParams = this._removeEmptyKeysFromObject(pannelParams)
            segmentParams.feedTitle = segmentParams.feedTitle ?? ''
            segmentParams.storeData = {
                ...(segmentParams.storeData || {}),
                ...((!product ||
                    (typeof product === 'object' && Object.keys(product).length === 0)) &&
                    this._emptyProductDetails())
            }
            segmentParams.storeData && (segmentParams.storeData.salePrice ??= null)

            window?.analytics?.track('Panel Selected', segmentParams, this.segmentContext?.context)
        } catch (error) {
            logger.error('segment- sendPanelSelectedEvent failed', error)
        }
    }

    /**
     * Tracks when a list of products is viewed, such as on a category or carousel page.
     *
     * @param {Object} productSearchResult - The product search result object returned from backend.
     * @param {Object} category - The category object representing the current category.
     * @param {Object} [carouselObject={}] - Optional metadata about the carousel component, if applicable.
     */
    async sendProductListViewed(productSearchResult, category, carouselObject, currentElement) {
        try {
            let carouselIndex = ''
            let plpFeedType = productSearchResult?.query ? 'Search' : 'Category'
            if (productSearchResult?.carousel) {
                carouselIndex = productSearchResult.carouselPageIndex
                productSearchResult = productSearchResult.activeProducts
                plpFeedType = ''
            }
            const {products, fullPath, feedTitle, ...restDomData} = getProductListViewedDomData(
                productSearchResult,
                this.segmentContext?.appOrigin,
                plpFeedType,
                currentElement
            )

            const [commonData, utmParamsData] = await Promise.all([
                commonParams(this.segmentContext),
                utmParams({})
            ])

            const productData = {
                ...restDomData,
                ...commonData,
                ...utmParamsData,
                listid: category?.id || '',
                category: category?.name || '',
                feedTitle: feedTitle || category?.name || '',
                products,
                pageId: fullPath,
                carouselPageIndex: carouselIndex
            }

            let segmentParams = this._removeEmptyKeysFromObject(productData)
            segmentParams.subStatus = this.segmentContext?.customerTier
                ? this.segmentContext.customerTier
                : 'null'

            window?.analytics?.track(
                'Product List Viewed',
                segmentParams,
                this.segmentContext?.context
            )
        } catch (error) {
            logger.error('segment- sendProductListViewed failed', error)
        }
    }

    /**
     * Tracks when page a is viewed.
     *
     * @param {string|null} prevPathRef - The url of previous page, or null if none
     */
    async pageEvent(prevPathRef) {
        try {
            const commonData = await commonParams(this.segmentContext)
            const productData = {
                ...commonData,
                path: window?.location?.pathname,
                referrer: prevPathRef
            }
            const segmentParams = this._removeEmptyKeysFromObject(productData)
            window.analytics.page(segmentParams)
        } catch (error) {
            logger.error('segment- pageEvent failed', error)
        }
    }

    /**
     * Tracks when a user views a minicart page.
     * @param {Object} productsData - The products in basket.
     * @param {Object} basket - current basket.
     */
    async sendCartViewed(productsData, basket) {
        try {
            const [commonParam, cartViewedDomData, utmParam] = await Promise.all([
                commonParams(this.segmentContext),
                getCartViewedDomData(productsData, basket),
                utmParams()
            ])

            const params = {
                cartId: basket?.basketId,
                currency: basket?.currency,
                ...commonParam,
                products: cartViewedDomData,
                ...utmParam
            }
            const segmentParams = this._removeEmptyKeysFromObject(params)

            window?.analytics?.track('Cart Viewed', segmentParams, this.segmentContext?.context)
        } catch (error) {
            logger.error('segment- sendCartViewed failed', error)
        }
    }

    /**
     * Tracks when a user click filter.
     * @param {Object} productSearchResultPrms - category data.
     * @param {Object} value - checked value
     */
    async sendFiltersApplied(productSearchResultPrms, attributeId, value, Filters) {
        try {
            const {...restDomData} = getFilterAppliedDomData(
                productSearchResultPrms,
                attributeId,
                value,
                Filters
            )
            const [commonData] = await Promise.all([commonParams(this.segmentContext)])
            const filtersAppliedData = {
                ...restDomData,
                ...commonData
            }
            const segmentParams = this._removeEmptyKeysFromObject(filtersAppliedData)

            window?.analytics?.track('Filters Applied', segmentParams, this.segmentContext?.context)
        } catch (error) {
            logger.error('segment- sendFiltersApplied failed', error)
        }
    }
}

/**
 * This Hook is to trigger all the segment events
 *
 */
const useSegment = () => {
    const segmentContext = useContext(SegmentContext)
    const segment = useMemo(() => new SegmentAPI(segmentContext), [segmentContext])

    return {
        async sendAddToCart(product, basket, event) {
            return segment.sendAddToCart(product, basket, event)
        },

        async sendAddToCartFromQuickView(product, basket) {
            return segment.sendAddToCart(product, basket)
        },

        async sendProductClick(product, productPosition, event) {
            return segment.sendProductClick(product, productPosition, event)
        },

        async sendProductToWishList(product, wishlist, productPosition, productDetail, event) {
            return segment.sendProductToWishList(
                product,
                wishlist,
                productPosition,
                productDetail,
                event
            )
        },

        async sendRemoveProductFromWishlist(product, wishlist, productPosition, productDetail) {
            return segment.sendRemoveProductFromWishlist(
                product,
                wishlist,
                productPosition,
                productDetail
            )
        },

        async sendPanelSelectedEvent(event, product, shopbyseries) {
            return segment.sendPanelSelectedEvent(event, product, shopbyseries)
        },

        async sendFooterEmailSubscribe(email) {
            return segment.sendFooterEmailSubscribe(email)
        },

        async sendBackInStock(eventName, email, product) {
            return segment.sendBackInStock(eventName, email, product)
        },

        async sendPromoClicked(promoDetails) {
            return segment.sendPromoClicked(promoDetails)
        },

        async sendProductViewed(event, product) {
            return segment.sendProductViewed(event, product)
        },

        async sendProductListViewed(productSearchResult, category, carouselObject, currentElement) {
            return segment.sendProductListViewed(
                productSearchResult,
                category,
                carouselObject,
                currentElement
            )
        },

        async sendCartViewed(productsData, basket) {
            return segment.sendCartViewed(productsData, basket)
        },

        async pageEvent(prevPathRef) {
            return segment.pageEvent(prevPathRef)
        },

        async sendFiltersApplied(productSearchResultPrms, attributeId, value, Filters) {
            return segment.sendFiltersApplied(productSearchResultPrms, attributeId, value, Filters)
        }
    }
}

export default useSegment
